<panel>
	@if (!editMode) {
		<h1>{{ 'order-details.header' | translate }}</h1>
	}
	@if (editMode) {
		<h1>{{ 'order-details.edit-mode.header' | translate }}</h1>
	}
	@if (!order || !violations) {
		<skeletons [defaultSkeletons]="5" />
	}
	@if (order && violations) {
		<scrollable>
			@if (!editMode) {
				<menu always-visible>
					<menuitem (click)="showSystemLog()" icon="stream">{{ 'order.show-system-log' | translate }}</menuitem>
					<menuitem *requiredCredential="Credentials.ManageCommission" [disabled]="!order || !order.approvedDate" (click)="toggleWithholdCommission()" icon="exclamation-triangle">{{ (order.withholdCommission ? 'order.remove-withhold-commission' : 'order.set-withhold-commission') | translate }}</menuitem>					
					<menuitem *requiredCredential="Credentials.ManageSales" [disabled]="!order || hasDeliveredPlans() || order.orderCancellations.length > 0" (click)="toggleAutoDelivery()" icon="exclamation-triangle">{{ (order.autoDelivery ? 'order.remove-auto-delivery' : 'order.set-auto-delivery') | translate }}</menuitem>
					<menuitem *requiredCredential="Credentials.ManageSales" [disabled]="!order || hasDeliveredPlans() || order.orderCancellations.length > 0" (click)="editOrderInstallments()" icon="coins">{{ 'order.edit-order-installments' | translate }}</menuitem>
					<menuitem *requiredCredential="Credentials.ManageSales" [disabled]="!order || !!order.approvedDate || hasExternalAdjustments()" (click)="editOrderLines()" icon="pen-to-square" [title]="hasExternalAdjustments() ? ('order.edit-order-lines.disable-due-to-discount' | translate) : null">{{ 'order.edit-order-lines' | translate }}</menuitem>
					<menuitem *requiredCredential="Credentials.ManageSales; and: hasExternalAdjustments()" [disabled]="!canEditExternalAdjustments()" (click)="editOrderExternals()" icon="sack-dollar">{{ 'order.edit-order-edit-special-external' | translate }}</menuitem>
					<menuitem *requiredCredential="Credentials.ManageSales; and: hasExternalAdjustments()" [disabled]="!canEditExternalAdjustments()" (click)="clearOrderExternals()" icon="xmark">{{ 'order.edit-order-clear-special-external' | translate }}</menuitem>
					<menuitem *requiredCredential="Credentials.ManageSales; and: !hasExternalAdjustments()" [disabled]="!canEditExternalAdjustments()" (click)="editOrderExternals()" icon="sack-dollar">{{ 'order.edit-order-add-special-external' | translate }}</menuitem>
					<menuitem *requiredCredential="Credentials.EditOrderSpecialConditions" [disabled]="!order" (click)="editOrderSpecialConditions()" icon="comments-dollar">{{ 'order.edit-order-special-conditions' | translate }}</menuitem>
				</menu>
			}
				<list-view [selectable]="false" emptyLabel="">
					@for (violation of violations; track violation) {
						<list-item alert statusIcon="exclamation-triangle">
							{{ violation.message | translate: violation.parameters }}
						</list-item>
					}
					@if (order.withholdCommission) {
						<list-item alert statusIcon="exclamation-triangle">
							{{ 'order.commission-is-withheld' | translate }}
						</list-item>
					}
					@if (!order.autoDelivery) {
						<list-item warning statusIcon="exclamation-triangle">
							{{ 'order.auto-delivery-is-off' | translate }}
						</list-item>
					}
					@if (!salesService.verifyContractUploaded(order)) {
						<list-item alert statusIcon="file-signature">
							{{ 'order.missing-contract' | translate }}
						</list-item>
					}
					@if (!companyService.getContactName(order.company)) {
						<list-item alert statusIcon="circle-user">
							{{ 'order.missing-contact-name' | translate }}
						</list-item>
					}
					@if (!companyService.getContactPhone(order.company)) {
						<list-item alert statusIcon="phone">
							{{ 'order.missing-contact-phone' | translate }}
						</list-item>
					}
					@if (!companyService.getContactEmail(order.company)) {
						<list-item alert statusIcon="envelope">
							{{ 'order.missing-contact-email' | translate }}
						</list-item>
					}
					@if (cancellation) {
						<list-item alert statusIcon="exclamation-triangle">
							{{ 'order.is-cancelled' | translate }}<br />{{ 'order.cancelled-reason' | translate: { reason: cancellation.reason?.name } }}<br />{{ 'order.cancelled-comment' | translate: { comment: cancellation.comment } }}
						</list-item>
					}
					@if (warningOrderMightBeDowngrade) {
						<list-item warning statusIcon="exclamation-triangle">
							{{ 'order.warning-might-be-downgrade' | translate }}
						</list-item>
					}
					@if (hasApproveOrderSpecialConditions) {
						<list-item alert statusIcon="exclamation-triangle">
							{{ 'order.has-special-conditions' | translate }} <a *requiredCredential="Credentials.EditOrderSpecialConditions" (click)="editOrderSpecialConditions()">{{ 'general.edit' | translate }}</a>
						</list-item>
					}
					@for (warning of orderWarnings; track warning.id) {
						<list-item warning statusIcon="exclamation-triangle">
							{{ 'sale.cancellation-warning' | translate }}:
							@if (warning.reason) {
								<span>{{ warning.reason.name }} </span>
							}
							@if (warning.comment != '') {
								<div>{{ 'general.comment' | translate }}: {{ warning.comment }}</div>
							}
						</list-item>
					}
					@if (hasExternalAdjustments()) {
						<list-item warning statusIcon="exclamation-triangle">
							{{ 'order.has-special-commission-calculations' | translate }} <a *requiredCredential="Credentials.ManageSales; and: canEditExternalAdjustments()" (click)="editOrderExternals()">{{ 'general.edit' | translate }}</a>
						</list-item>
					}
				</list-view>

			<editable-content #orderDetailsEditor *editableData="let edit from order" [clickToEdit]="false" (valueChanged)="editOrderDetails($event)">
				<display-content>
					<section>
						<menu>
							<menuitem *requiredCredential="Credentials.EditSalesDetails; and: order && !editMode" (click)="orderDetailsEditor.beginEdit()"><icon type="user-edit" />{{ 'order.edit-details' | translate }}</menuitem>
						</menu>
						<text-group>
							<span>{{ 'order.company-name' | translate }}</span>
							<span>{{ order.company?.name }}</span>

							<span>{{ 'order.reference-number' | translate }}</span>
							<span>{{ order.referenceNumber }}</span>

							<span>{{ 'order.seller' | translate }}</span>
							<span>{{ order.sellerId | user: 'name' }}</span>

							<span>{{ 'order.sales-team' | translate }}</span>
							<span>{{ order.salesTeamId | team }}</span>

							<span>{{ 'order.brand' | translate }}</span>
							<span>{{ order.salesTeamId | team:'brand' }} <icon [attr.brand]="order.salesTeamId | team:'brandShortName'" type="logo" small /></span>

							<span>{{ 'order.lead-user' | translate }}</span>
							<span>{{ order.leadUserId | user: 'name' }}</span>

							<span>{{ 'order.sign-date' | translate }}</span>
							@if (order.signDate) {
								<span>{{ order.signDate | customDate: 'shortDateText' }}</span>
							}
							@else {
								<span>{{ 'sale.missing-sign-date' | translate }}</span>
							}
							<span>{{ 'order.approved-date' | translate }}</span>
							@if (order.approvedDate) {
								<span>{{ order.approvedDate | customDate: 'shortDateText' }}</span>
							}
							@else {
								<span>{{ 'sale.not-approved-yet' | translate }}</span>
							}
							<spacer />

							<span>{{ 'order.attachments' | translate }}</span>
							<span>
								@for (attachment of order.saleAttachments; track attachment.id) {
									<file-download container="contracts" [fileName]="attachment.fileName" icon="file-signature">
										@if (canEditSaleAttachments()) {
											<button small reject (click)="deleteContract(attachment.id!)" [loader]="false" [ignoreValidation]="true"><icon type="xmark" /></button>
										}
									</file-download><br />
								}
								@if (!order.approvedDate) {
									@if (!showUpload && order.saleAttachments.length != 0) {
										@if (canEditSaleAttachments()) {
											<button small confirm (click)="showUpload = true" [loader]="false" [ignoreValidation]="true"><icon type="plus" /></button>
										}
									}
									@if (showUpload || order.saleAttachments.length == 0) {
										<file-upload
												 [label]="'order.upload-contract' | translate"
												 [hideUploadedFiles]="true"											 
												 (filesChanged)="showUpload = false"
												 accept=".msg,.jpg,.pdf"
												 [uploadUrl]="'/api/upload/create-sale-attachments/order/' + orderId"
												 [finalizeUrl]="'/api/upload/finalize-sale-attachments/order/' + orderId" />
									}
								}
							</span>
						</text-group>
					</section>
				</display-content>
				<edit-content>
					@if (edit.value) {
						<section>
							<custom-select [label]="'order.seller' | translate"
										   [selectList]="userList"
										   [(value)]="edit.value.sellerId"
										   [allowAutoFocus]="false"
										   [required]="true" />
							<custom-select [label]="'order.sales-team' | translate"
										   [selectList]="saleTeamList"
										   [(value)]="edit.value.salesTeamId"
										   [allowAutoFocus]="false"
										   [autoSort]="true"
										   [required]="true" />
							<custom-select [label]="'order.lead-user' | translate"
										   [selectList]="userList"
										   [(value)]="edit.value.leadUserId"
										   [allowAutoFocus]="false" />
							<date-input [label]="'order.sign-date' | translate"
										[(value)]="edit.value.signDate"
										[required]="true"
										[maxDate]="maxSignDate" />
						</section>
					}
				</edit-content>
			</editable-content>

			<section>
				<button circle (click)="showPanel = 'company'" [selected]="showPanel == 'company'" [loader]="false" [active]="!!order.company"><icon type="building" /></button>
				<button circle (click)="showPanel = 'questionnaire'" [selected]="showPanel == 'questionnaire'" [loader]="false"><icon type="clipboard-question" /></button>
				<button circle (click)="showPanel = 'communication'" [selected]="showPanel == 'communication'" [loader]="false" [active]="order!.orderWarnings.length > 0"><icon type="comments" /></button>
			</section>

			<h3>{{ 'order-details.summary' | translate }}</h3>
			<section three-columns>
				<summary [label]="'order-details.total-price' | translate"><icon small type="circle-info" [trigger-soft-popup]="popupTotalPrice" /> {{ getTotalPrice() + totalDiscount + getTotalAdjustmentAmount() | currency }}</summary>
				<summary [label]="'order-details.remaining-on-superseded-plan' | translate">{{ calculateSupersededRemainingAmountForOrder() | currency }}</summary>
				<summary [label]="'order-details.total-sales-value' | translate"><icon small type="circle-info" [trigger-soft-popup]="popupSalesValue" /> {{ getTotalPrice() + totalDiscount + getTotalAdjustmentAmount() + calculateSupersededRemainingAmountForOrder() | currency }}</summary>
				@if (!editMode) {
					<summary [label]="'order-details.commission.acv-increase' | translate"><icon small type="circle-info" [trigger-soft-popup]="popupCommission" /> {{ salesService.getAcvIncreaseCommission(order.orderLines) | currency }}</summary>
					<summary [label]="'order-details.commission.acv-extend' | translate"><icon small type="circle-info" [trigger-soft-popup]="popupCommission" /> {{ salesService.getAcvExtendCommission(order.orderLines) | currency }}</summary>
					<summary [label]="'order-details.commission.up-front' | translate">{{ salesService.getUpFrontCommission(order.orderLines) | currency }}</summary>
				}
				<soft-popup #popupTotalPrice>
					<section>
						<text-group>
							<span>{{ 'order-details.popup-totalprice.full-price' | translate }}</span>
							<span align-right>{{ getTotalPrice() | currency }}</span>

							<span>{{ 'order-details.popup-totalprice.discount' | translate }}</span>
							<span align-right>{{ totalDiscount | currency }}</span>

							<span>{{ 'order-details.popup-totalprice.adjustment' | translate }}</span>
							<span align-right>{{ getTotalAdjustmentAmount() | currency }}</span>

							<span bold>{{ 'order-details.popup-totalprice.sum' | translate }}</span>
							<span align-right bold>{{ (getTotalPrice() + totalDiscount + getTotalAdjustmentAmount()) | currency }}</span>
						</text-group>
					</section>
				</soft-popup>
				<soft-popup #popupSalesValue>
					<section>
						<text-group>
							<span>{{ 'order-details.popup-salesvalue.full-price' | translate }}</span>
							<span align-right>{{ getTotalPrice() | currency }}</span>

							<span>{{ 'order-details.popup-salesvalue.discount' | translate }}</span>
							<span align-right>{{ totalDiscount | currency }}</span>

							<span>{{ 'order-details.popup-salesvalue.adjustment' | translate }}</span>
							<span align-right>{{ getTotalAdjustmentAmount() | currency }}</span>

							<span>{{ 'order-details.popup-salesvalue.remaining' | translate }}</span>
							<span align-right>{{calculateSupersededRemainingAmountForOrder() | currency }}</span>

							<span bold>{{ 'order-details.popup-salesvalue.sum' | translate }}</span>
							<span align-right bold>{{ (getTotalPrice() + totalDiscount + getTotalAdjustmentAmount() + calculateSupersededRemainingAmountForOrder()) | currency }}</span>
						</text-group>
					</section>
				</soft-popup>
				<soft-popup [width]="400" #popupCommission>
					<order-commission-details [order]="order"></order-commission-details>
				</soft-popup>
			</section>

			<order-lines [orderLines]="order.orderLines"
						 [editMode]="editMode"
						 [defaultNumberOfInstallments]="defaultNumberOfInstallments"
						 [discountReason]="discountReason"
						 (orderLineChanged)="recalculateStartupAmounts()"></order-lines>
			@if (!hasExternalAdjustments() && (editMode || order.orderProductPlanDiscontinuations.length > 0)) {
				<order-plan-discontinuations [discontinuations]="order.orderProductPlanDiscontinuations"
											 [companyId]="order.company?.id"
											 [editMode]="editMode"
											 (discontinuationChanged)="recalculateStartupAmounts()" />
			}
			@if (order.saleAgreement) {
				<section one-column>
					<h3><icon type="file" />{{ 'sale.approve.special-agreement' | translate }} {{ order.saleAgreement.owner!.name }} - {{ order.saleAgreement.created | customDate: 'shortDate' }}:</h3>
					<div class="text-block">{{ order.saleAgreement.comment }}</div>
				</section>
			}
		</scrollable>
	}

	<section *requiredCredential="Credentials.ManageSales; and: !editMode && order && order!.orderCancellations.length == 0 && !hasDeliveredPlans()">
		<button #buttonReject reject [ignoreValidation]="true" [active]="!hasDeliveredPlans()" (click)="createOrderCancellation()">{{ 'sale.reject' | translate }}</button>
		<button #buttonWarning [ignoreValidation]="true" (click)="hasOpenWarning() ? removeOrderWarning() : createOrderWarning()">{{ (hasOpenWarning() ? 'sale.remove-order-warning' : 'sale.create-order-warning') | translate }}</button>
		<button #buttonConfirm confirm [active]="canApprove()" [ignoreValidation]="true" (click)="approveOrder()">{{ 'sale.approve' | translate }}</button>
	</section>
	@if (editMode) {
		<section>
			<button ghost [ignoreValidation]="true" (click)="cancelEditMode()">{{ 'sale.edit-mode.cancel' | translate }}</button>
			<button confirm (click)="saveOrderLineChanges()" [active]="order!.orderLines.length > 0">{{ 'sale.edit-mode.confirm' | translate }}</button>
		</section>
	}
</panel>

@if (order && order.company) {
	<panel width="500" [hidden]="showPanel != 'company'">
		<h1>{{ 'company.contact-information' | translate }}</h1>
		<company-information [company]="order.company" />
		<list-contact [company]="order.company" />
	</panel>
}

@if (order) {
	<panel width="700" minWidth="600" [hidden]="showPanel != 'questionnaire'">
		<h1>{{ 'order.questionnaire' | translate }}</h1>
		@for (questionnaire of questionnaires; track questionnaire) {
			<section scrollable>
				<questionnaire [questionnaireId]="questionnaire.id" 
								[orderLines]="order.orderLines"
								[showCategoryDetails]="false"
								[editable]="true" />
			</section>
		}
	</panel>
}

@if (order) {
	<panel width="500" [hidden]="showPanel != 'communication'">
		<h1>{{ 'order.communication-log' | translate }}</h1>
		<scrollable>
			@for (warning of order.orderWarnings; track warning) {
				@if (warning.taskflowId) {
					<task-action-log [company]="order.company" [taskflowId]="warning.taskflowId" [allowNewComment]="!warning.closed" [allowNewMail]="false" [showFilters]="false" />
				}
			}
		</scrollable>
	</panel>
}
